import { FormProvider } from '@/context/FormProvider';

import {
  type AgentCreateFormValues,
  agentCreateSchema,
  type AgentUpdateFormValues,
  agentUpdateSchema,
  type WebhookConfigFormValues,
} from '@/schemas/agent.schema';

import { SENTIMENT_STARTER_PROMPT } from '@/constants';

import {
  isDataExtractionBuilderCompatible,
  isDataExtractionRootArraySchema,
  mapJsonSchemaToDataExtractionProperties,
  stringifyDataExtractionJsonSchema,
} from '@/lib/data-extraction';
import { mapJsonSchemaToSentimentTags, SENTIMENT_DEFAULT_TAGS } from '@/lib/sentiment';

import type { AgentDetailResponse } from '@/types/agent.type';

type Props<T> = {
  children: React.ReactNode;
  initialValues?: T;
  onSubmit: (values: AgentUpdateFormValues) => void;
};

export const AgentUpdateFormProvider = ({
  children,
  initialValues,
  onSubmit,
}: Props<AgentDetailResponse>) => {
  const extractionSchema = initialValues?.data_extraction?.json_schema;
  const canUseBuilderSchema = isDataExtractionBuilderCompatible(extractionSchema);
  const extractionRootIsArray = isDataExtractionRootArraySchema(extractionSchema);

  const savedSentimentTags = mapJsonSchemaToSentimentTags(initialValues?.sentiment?.json_schema);

  const _initialValues: AgentUpdateFormValues = {
    agent_name: initialValues?.agent_name || '',
    webhook_config: initialValues?.call_settings?.webhook_config as WebhookConfigFormValues,
    temperature: initialValues?.temperature || 0.1,
    system_prompt: initialValues?.system_prompt?.prompt || '',
    prompt_variables: initialValues?.system_prompt?.variables || [],
    ring_duration: initialValues?.call_settings?.ring_duration || 15,
    max_call_duration: initialValues?.call_settings?.max_call_duration || 300,
    end_call_silence: initialValues?.call_settings?.end_call_on_silence || 5,
    is_call_recording_enabled: initialValues?.call_settings?.is_call_recording_enabled ?? false,
    voice_id: initialValues?.voice?.id || '',

    summary_prompt: initialValues?.summary?.prompt || '',
    summary_prompt_variables: initialValues?.summary?.variables || [],

    data_extraction_prompt: initialValues?.data_extraction?.prompt || '',
    data_extraction_prompt_variables: initialValues?.data_extraction?.variables || [],
    data_extraction_schema_mode: extractionSchema && !canUseBuilderSchema ? 'json' : 'builder',
    data_extraction_json_schema: stringifyDataExtractionJsonSchema(extractionSchema),
    data_extraction_root_is_array: extractionRootIsArray,
    data_extraction_properties: canUseBuilderSchema
      ? mapJsonSchemaToDataExtractionProperties(extractionSchema)
      : [],

    sentiment_prompt: initialValues?.sentiment?.prompt || SENTIMENT_STARTER_PROMPT,
    sentiment_prompt_variables: initialValues?.sentiment?.variables || [],
    sentiment_tags: savedSentimentTags.length > 0 ? savedSentimentTags : SENTIMENT_DEFAULT_TAGS,
  };

  return (
    <FormProvider
      initialValues={_initialValues}
      validationSchema={agentUpdateSchema}
      onSubmit={onSubmit}
    >
      {children}
    </FormProvider>
  );
};

export const AgentCreateFormProvider = ({ children, onSubmit }: Props<AgentCreateFormValues>) => {
  const initialValues: AgentCreateFormValues = {
    agent_name: '',
    description: '',
    template_id: null,
    voice_id: '',
  };

  return (
    <FormProvider
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={agentCreateSchema}
    >
      {children}
    </FormProvider>
  );
};
